import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Badge } from 'flowbite-react';
import Modals from '../../components/Modal';
import DeliverableForm from './DeliverableForm';
import LoadingSpinner from '../../components/LoadingSpinner';
import MergetagManager from './MergetagManager';
import StageManager from './StageManager';

const Deliverables = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deliverables, setDeliverables] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMergetagModalOpen, setIsMergetagModalOpen] = useState(false);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [openStageIds, setOpenStageIds] = useState([]);
  const [editingDeliverable, setEditingDeliverable] = useState(null);

  const openModal = (deliverable = null) => {
    setEditingDeliverable(deliverable);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDeliverable(null);
  };
  const openMergetagModal = () => setIsMergetagModalOpen(true);
  const closeMergetagModal = () => setIsMergetagModalOpen(false);
  const openStageModal = () => setIsStageModalOpen(true);
  const closeStageModal = () => setIsStageModalOpen(false);

  const fetchDeliverables = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/deliverables`,
        { withCredentials: true },
      );
      setDeliverables(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliverables();
  }, []);

  const handleSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (editingDeliverable) {
        const response = await axios.put(
          `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/deliverable/${editingDeliverable.id}/update`,
          data,
          { withCredentials: true },
        );
        const updated = response.data.deliverable;
        setDeliverables(
          deliverables.map((d) => (d.id === updated.id ? updated : d)),
        );
      } else {
        const response = await axios.post(
          `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/deliverable/create`,
          data,
          { withCredentials: true },
        );
        setDeliverables([response.data.deliverable, ...deliverables]);
      }
      closeModal();
    } catch (error) {
      console.error('Error al guardar entregable:', error);
      alert('Error al guardar entregable');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (deliverableId) => {
    const confirm = window.confirm('¿Estás seguro de eliminar este entregable?');
    if (!confirm) return;
    setIsLoading(true);
    try {
      await axios.delete(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/deliverable/${deliverableId}/delete`,
        { withCredentials: true },
      );
      setDeliverables(deliverables.filter((d) => d.id !== deliverableId));
    } catch (error) {
      console.error('Error al eliminar entregable:', error);
      alert('Error al eliminar entregable');
    } finally {
      setIsLoading(false);
    }
  };

  const groupedByStage = deliverables.reduce((acc, d) => {
    const key = d.stage?.id || 'none';
    if (!acc[key]) {
      acc[key] = {
        stage: d.stage || null,
        items: [],
      };
    }
    acc[key].items.push(d);
    return acc;
  }, {});

  const toggleStage = (stageKey) => {
    setOpenStageIds((prev) =>
      prev.includes(stageKey)
        ? prev.filter((id) => id !== stageKey)
        : [...prev, stageKey],
    );
  };

  const stageKeys = Object.keys(groupedByStage);

  return (
    <div className="p-4 mt-4">
      {isLoading && <LoadingSpinner message="Procesando..." size="lg" color="blue" />}

      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Entregables</h1>
        <div className="flex gap-2">
          <Button color="light" onClick={openStageModal}>
            Etapas
          </Button>
          <Button color="light" onClick={openMergetagModal}>
            Mergetags
          </Button>
          <Button color="gray" onClick={() => openModal(null)}>
            Nuevo
          </Button>
        </div>
      </div>

      <p className="text-gray-600 mb-4">
        Plantillas de documentos para compartir mediante enlaces privados.
      </p>
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <div className="space-y-4">
        {stageKeys.map((key) => {
          const group = groupedByStage[key];
          const isOpen = openStageIds.includes(key);
          const stageName = group.stage?.name || 'Sin etapa';
          const stageDescription = group.stage?.description || '';
          return (
            <div
              key={key}
              className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
            >
              <button
                type="button"
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
                onClick={() => toggleStage(key)}
              >
                <div className="flex flex-col text-left">
                  <span className="text-sm font-semibold text-gray-800">
                    {stageName}
                  </span>
                  {stageDescription && (
                    <span className="text-xs text-gray-500">
                      {stageDescription}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {group.items.length} entregable(s)
                </span>
              </button>
              {isOpen && (
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 p-4 border-t border-gray-100">
                  {group.items.map((d) => (
                    <div
                      key={d.id}
                      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col overflow-hidden"
                    >
                      <div className="px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 flex justify-between items-center">
                        <div className="flex flex-col overflow-hidden">
                          <span className="text-sm font-semibold truncate">
                            {d.name}
                          </span>
                        </div>
                        <Badge
                          color="success"
                          className="bg-white/10 border border-white/30 text-[11px] px-2 py-0.5 rounded-full"
                        >
                          {d.project_type}
                        </Badge>
                      </div>
                      <div className="p-4 space-y-2 text-sm text-gray-700">
                        <p className="line-clamp-3">
                          {d.description || 'Sin descripción'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Creado: {new Date(d.created_at).toLocaleString()}
                        </p>
                        <div className="flex justify-end mt-2">
                          <Button
                            size="xs"
                            color="light"
                            className="border border-gray-300 text-gray-700 hover:bg-gray-100"
                            onClick={async () => {
                              try {
                                const response = await axios.post(
                                  `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/deliverable/${d.id}/generate-link`,
                                  {},
                                  { withCredentials: true },
                                );
                                const token = response.data.token;
                                const base = (import.meta.env.VITE_ENV == 'development')
                                  ? import.meta.env.VITE_BACKEND_LOCAL
                                  : import.meta.env.VITE_BACKEND;
                                if (token && base) {
                                  const backendBase = base.replace(/\/$/, '');
                                  const liveLink = `${backendBase}/public/deliverable/${token}`;
                                  window.open(liveLink, '_blank', 'noopener,noreferrer');
                                } else {
                                  alert('No se pudo obtener el enlace del entregable');
                                }
                              } catch (err) {
                                console.error('Error al generar enlace:', err);
                                alert('Error al generar enlace');
                              }
                            }}
                          >
                            Ver el PDF en vivo
                          </Button>
                          <Button
                            size="xs"
                            color="light"
                            className="ml-2 border border-gray-300 text-gray-700 hover:bg-gray-100"
                            onClick={() => openModal(d)}
                          >
                            Editar
                          </Button>
                          <Button
                            size="xs"
                            color="failure"
                            className="ml-2"
                            onClick={() => handleDelete(d.id)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {stageKeys.length === 0 && !isLoading && (
          <div
            className="text-gray-500"
          >
            No hay entregables creados.
          </div>
        )}
      </div>

      <Modals
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingDeliverable ? 'Editar entregable' : 'Nuevo entregable'}
        modalSize="4xl"
      >
        <DeliverableForm onSubmit={handleSubmit} onClose={closeModal} deliverable={editingDeliverable} />
      </Modals>

      <Modals
        isOpen={isMergetagModalOpen}
        onClose={closeMergetagModal}
        title="Mergetags"
        modalSize="4xl"
      >
        <MergetagManager />
      </Modals>

      <Modals
        isOpen={isStageModalOpen}
        onClose={closeStageModal}
        title="Etapas"
        modalSize="4xl"
      >
        <StageManager />
      </Modals>
    </div>
  );
};

export default Deliverables;
